export const ACTION_LABELS = {
  TIMESHEET_CREATED: 'Relevé créé',
  TIMESHEET_DELETED: 'Relevé supprimé',
  TIMESHEET_USER_CHANGED: 'Intérimaire réaffecté',
  DAY_UPDATED: 'Heures saisies',
  DAY_UPDATED_ADMIN: 'Heures modifiées par un admin',
  DAY_VALIDATED_COMPANY: 'Jour traité par l’entreprise',
  DAY_SUGGESTED_COMPANY: 'Heures proposées par l’entreprise',
  WEEK_VALIDATED: 'Semaine validée par l’intérimaire',
  WEEK_VALIDATED_COMPANY: 'Semaine traitée par l’entreprise',
  STATUS_CHANGED_ADMIN: 'Statut changé par un admin',
  BESTT_IMPORT_FLAGGED: 'Marquage import BeSTT',
  PERIOD_MODIFIED: 'Période de mission modifiée',
  TOTALS_RECALCULATED: 'Totaux recalculés',
  ROLLBACK: 'Action annulée',
};

export const ACTOR_LABELS = {
  user: 'intérimaire',
  company: 'entreprise',
  employee: 'admin',
  system: 'Automatique',
};

export const SOURCE_LABELS = {
  'web-interim': 'espace intérimaire',
  'mobile-interim': 'application intérimaire',
  'mobile-pro': 'application entreprise',
  'web-company': 'espace entreprise',
  'web-admin': 'back-office',
  cron: 'tâche planifiée',
  'import-bestt': 'import BeSTT',
  script: 'script',
};

export const FIELD_LABELS = {
  validated: 'Statut',
  day_hours: 'Heures du jour',
  day_hours_ent: 'Heures retenues',
  total_hours: 'Total du relevé',
  total_hours_ent: 'Total retenu',
  periode_one: 'Matin',
  periode_two: 'Après-midi',
  periode_three: 'Soir',
  periode_one_company: 'Matin (entreprise)',
  periode_two_company: 'Après-midi (entreprise)',
  periode_three_company: 'Soir (entreprise)',
  periode_start: 'Début de période',
  periode_end: 'Fin de période',
  is_imported_bestt: 'Importé dans BeSTT',
  id_user: 'Intérimaire',
};

const STATUS_LABELS = {
  0: 'Non complété',
  1: 'À vérifier',
  2: 'Litige',
  3: 'Vérifié',
};

const DESTRUCTIVE_ACTIONS = ['TIMESHEET_DELETED', 'PERIOD_MODIFIED'];

const EMPTY = '—';

const formatPeriod = (value) => {
  if (!value || (!value.start && !value.end)) return EMPTY;
  return `${value.start || EMPTY} – ${value.end || EMPTY}`;
};

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (field === 'validated') return STATUS_LABELS[Number(value)] ?? String(value);
  if (field === 'is_imported_bestt') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') return formatPeriod(value);
  return String(value);
};

/** One readable line instead of three badges: who, in what capacity, from where. */
export const describeActor = (event) => {
  const type = event?.actor?.type;
  const source = SOURCE_LABELS[event?.source] || event?.source || '';
  if (type === 'system' || !event?.actor?.displayName) {
    return [ACTOR_LABELS.system, source].filter(Boolean).join(' · ');
  }
  return [event.actor.displayName, ACTOR_LABELS[type], source]
    .filter(Boolean)
    .join(' · ');
};

/** Fields holding an identifier. Printing a raw ObjectId tells an admin
 *  nothing, so these are stated in words instead of shown as a diff. */
const OPAQUE_FIELDS = new Set(['id_user']);

const describeOpaqueChange = (change) => {
  const had = change?.before !== null && change?.before !== undefined;
  const has = change?.after !== null && change?.after !== undefined;
  const label = FIELD_LABELS[change?.field] || change?.field || '';
  if (had && has) return { label, text: `${label} remplacé` };
  if (has) return { label, text: `${label} assigné` };
  return { label, text: `${label} retiré` };
};

export const describeChange = (change) => {
  if (OPAQUE_FIELDS.has(change?.field)) return describeOpaqueChange(change);

  const before = formatValue(change?.field, change?.before);
  const after = formatValue(change?.field, change?.after);
  return {
    label: FIELD_LABELS[change?.field] || change?.field || '',
    before,
    after,
    // A first entry has no previous value to strike out: showing "— → 09:00"
    // reads as a missing datum when it only means the day was empty.
    isCreation: before === EMPTY && after !== EMPTY,
    isRemoval: before !== EMPTY && after === EMPTY,
  };
};

/** A row with nothing on either side is noise: the event already says what
 *  happened, and an empty "— → —" only raises questions. */
const carriesInformation = (described) =>
  Boolean(described.text) ||
  described.before !== EMPTY ||
  described.after !== EMPTY;

/** Derived fields are consequences, not decisions: the UI demotes them. */
export const visibleChanges = (event) => {
  const changes = event?.changes || [];
  const describe = (list) => list.map(describeChange).filter(carriesInformation);
  return {
    decided: describe(changes.filter((c) => !c.derived)),
    derived: describe(changes.filter((c) => c.derived)),
  };
};

export const isSystemEvent = (event) =>
  event?.action === 'TOTALS_RECALCULATED' || event?.source === 'cron';

export const isDestructive = (event) =>
  DESTRUCTIVE_ACTIONS.includes(event?.action);

const STATUS_DISPUTE = 2;
const STATUS_VERIFIED = 3;

const statusSide = (event, side) => {
  const change = (event?.changes || []).find((c) => c.field === 'validated');
  if (!change) return null;
  const value = change[side];
  return value === null || value === undefined ? null : Number(value);
};

const plural = (count, one, many) => (count > 1 ? many(count) : one);

/**
 * Phrases continuing the actor's name: "Camille Martin · … / a vérifié le
 * relevé". ACTION_LABELS stays as it is — `ficheHour` prints it standalone,
 * where a verb phrase with no subject would read as a fragment.
 *
 * Some actions describe two different facts depending on where they left the
 * sheet: the company's weekly gesture is a verification or the opening of a
 * dispute, and only the resulting status separates them.
 */
const ACTION_PHRASES = {
  TIMESHEET_CREATED: () => 'a créé le relevé',
  TIMESHEET_DELETED: () => 'a supprimé le relevé',
  TIMESHEET_USER_CHANGED: () => 'a réaffecté le relevé à un autre intérimaire',
  DAY_UPDATED: (event, days) =>
    plural(days, 'a complété une journée', (n) => `a complété ${n} journées`),
  DAY_UPDATED_ADMIN: (event, days) =>
    plural(
      days,
      "a corrigé les heures d'une journée",
      (n) => `a corrigé les heures de ${n} journées`
    ),
  DAY_SUGGESTED_COMPANY: (event, days) =>
    plural(
      days,
      "a proposé d'autres heures",
      (n) => `a proposé d'autres heures sur ${n} journées`
    ),
  DAY_VALIDATED_COMPANY: (event, days) =>
    statusSide(event, 'after') === STATUS_DISPUTE
      ? plural(days, 'a créé un litige', (n) => `a créé un litige sur ${n} journées`)
      : plural(days, 'a validé une journée', (n) => `a validé ${n} journées`),
  WEEK_VALIDATED: () => "a envoyé son relevé d'heures à l'entreprise",
  WEEK_VALIDATED_COMPANY: (event) =>
    statusSide(event, 'after') === STATUS_DISPUTE
      ? 'a créé un litige'
      : 'a vérifié le relevé',
  STATUS_CHANGED_ADMIN: (event) => {
    const before = statusSide(event, 'before');
    const after = statusSide(event, 'after');
    if (before === STATUS_DISPUTE && after !== STATUS_DISPUTE)
      return 'a corrigé le litige';
    if (after === STATUS_DISPUTE) return 'a mis le relevé en litige';
    if (after === STATUS_VERIFIED) return 'a vérifié le relevé';
    return 'a changé le statut du relevé';
  },
  BESTT_IMPORT_FLAGGED: (event) =>
    (event?.changes || []).some(
      (c) => c.field === 'is_imported_bestt' && !c.after
    )
      ? 'a retiré le marquage « Importé sur BeSTT »'
      : 'a indiqué le relevé comme « Importé sur BeSTT »',
  PERIOD_MODIFIED: () => 'a modifié la période de la mission',
  TOTALS_RECALCULATED: () => 'a recalculé les totaux du relevé',
  ROLLBACK: () => 'a annulé une action',
};

/** `dayCount` lets one entry standing for several days agree in number. */
export const describeAction = (event, dayCount = 1) => {
  const phrase = ACTION_PHRASES[event?.action];
  if (phrase) return phrase(event, dayCount || 1);
  return ACTION_LABELS[event?.action] || event?.action || '';
};

/**
 * Who acted, on its own — the timeline gives the name its own slot.
 *
 * A name is trusted over `actor.type`: several actions record a back-office
 * gesture as 'system', and calling a named person "Système" hides the very gap
 * this panel exists to expose.
 */
export const actorName = (event) => event?.actor?.displayName || 'Système';

/** In what capacity and from where, to sit as a caption beside the action. */
export const actorContext = (event) => {
  const source = SOURCE_LABELS[event?.source] || event?.source || '';
  const type = event?.actor?.type;
  // Same mistrust: with a name in hand the source places the action better than
  // a type that may well say 'system' about an admin.
  if (!event?.actor?.displayName || type === 'system') return source;
  return [ACTOR_LABELS[type], source].filter(Boolean).join(' · ');
};
